import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';

export function useNavigation() {
  const router = useRouter();
  const store = useStore();
  const toast = useToast();

  // Navigation vers les détails d'une livraison
  const navigateToDelivery = async (delivery) => {
    try {
      // Stocker la livraison actuelle dans le store avant la navigation
      await store.dispatch('setCurrentDelivery', delivery);
      
      // Importer de manière asynchrone pour éviter les problèmes de dépendances circulaires
      const { syncService } = await import('@/services/syncService');
      
      // Explicitement mettre en cache cette livraison spécifique avant la navigation
      // pour garantir que ses données sont disponibles hors-ligne
      if (isShopDelivery(delivery)) {
        await syncService.cacheShopDelivery(delivery.id, delivery);
      } else {
        await syncService.cacheDelivery(delivery.id, delivery);
      }

      // Navigation avec gestion du type de livraison (shop ou régulière)
      if (isShopDelivery(delivery)) {
        console.log("Navigation vers shop delivery:", delivery.id);
        // Pour les livraisons shop, on navigue vers la page spécifique
        await router.push({
          name: 'shop-delivery-detail',
          params: { id: delivery.id }
        });
      } else {
        console.log("Navigation vers delivery standard:", delivery.id);
        // Navigation normale pour les livraisons standard
        await router.push({
          name: 'delivery-detail',
          params: { id: delivery.id }
        });
      }
    } catch (error) {
      console.error('Erreur de navigation:', error);
      // Afficher un message à l'utilisateur
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de charger les détails de la livraison',
        life: 3000
      });
    }
  };

  // Fonction utilitaire pour déterminer si une livraison est de type shop
  const isShopDelivery = (delivery) => {
    return (
      delivery?.shop_delivery_address || 
      delivery?.shop === true || 
      delivery?.type === 'shop' ||
      (delivery?.attributes && delivery.attributes.includes('shop'))
    );
  };

  // Fonction pour ouvrir Waze vers le dépôt
  const openNavigationToDepot = (latitude, longitude, address) => {
    const lat = latitude || 48.765872955322266;
    const lng = longitude || 1.9921187162399292;
    const addressParam = encodeURIComponent(address || '9 rue Nicolas Copernic, 78190 Trappes');

    // Pour tous les appareils mobiles, on essaie d'ouvrir l'application Waze native
    if (lat && lng) {
      // On tente d'abord d'ouvrir avec le protocole waze://
      const wazeUrl = `waze://?ll=${lat},${lng}&navigate=yes`;
      
      // Technique avec iframe invisible pour ouvrir l'app
      const wazeIntent = document.createElement('iframe');
      wazeIntent.style.display = 'none';
      document.body.appendChild(wazeIntent);
      wazeIntent.src = wazeUrl;
      
      // Fallback après un court délai si l'app ne s'ouvre pas
      setTimeout(() => {
        // On supprime l'iframe
        document.body.removeChild(wazeIntent);
        
        // URL web de Waze comme solution de repli - ouvrir dans un nouvel onglet
        const webWazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
        
        // Ouvrir dans un nouvel onglet au lieu de rediriger
        window.open(webWazeUrl, '_blank');
      }, 500);
    } else if (addressParam) {
      // Si pas de coordonnées, on essaie avec l'adresse
      const wazeUrl = `waze://?q=${addressParam}&navigate=yes`;
      
      const wazeIntent = document.createElement('iframe');
      wazeIntent.style.display = 'none';
      document.body.appendChild(wazeIntent);
      wazeIntent.src = wazeUrl;
      
      setTimeout(() => {
        document.body.removeChild(wazeIntent);
        const webWazeUrl = `https://waze.com/ul?q=${addressParam}&navigate=yes`;
        
        // Ouvrir dans un nouvel onglet au lieu de rediriger
        window.open(webWazeUrl, '_blank');
      }, 500);
    } else {
      // Fallback pour les ordinateurs de bureau ou si aucune coordonnée n'est disponible
      window.open(`https://www.google.com/maps/search/?api=1&query=${addressParam}`, '_blank');
    }
  };

  // Fonction pour rafraîchir complètement la page (équivalent à F5)
  const refreshPage = () => {
    toast.add({
      severity: 'info',
      summary: 'Actualisation',
      detail: 'Actualisation de la page en cours...',
      life: 2000
    });
    
    // Petit délai pour permettre à l'utilisateur de voir le toast
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return {
    navigateToDelivery,
    openNavigationToDepot,
    refreshPage
  };
} 